import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatTile, SectionHeader, Pill } from "@/components/Brand";
import {
  MapPin,
  Building2,
  Users,
  ClipboardList,
  TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminOverview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/overview").then((r) => setData(r.data));
  }, []);

  if (!data)
    return <div className="text-slate-500 text-sm">Loading overview…</div>;

  return (
    <div>
      <SectionHeader eyebrow="Admin" title="Election Intelligence Overview" />

      <div className="wi-stat-row mb-8">
        <StatTile label="Total Wards" value={data.total_wards} icon={MapPin} />
        <StatTile label="Total Booths" value={data.total_booths} icon={Building2} />
        <StatTile label="Active Surveyors" value={data.active_surveyors} icon={Users} />
        <StatTile
          label="Avg Surveys / Booth"
          value={data.avg_coverage}
          icon={ClipboardList}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="wi-card p-6 lg:col-span-2">
          <SectionHeader
            eyebrow="Coverage"
            title="Ward-wise survey completion"
          />
          <div className="space-y-4">
            {data.ward_coverage.map((w) => (
              <div
                key={w.ward_id}
                className="grid grid-cols-12 gap-3 items-center"
                data-testid={`coverage-row-${w.ward_name.toLowerCase()}`}
              >
                <div className="col-span-4 md:col-span-3">
                  <div className="font-medium text-slate-900 text-sm">
                    {w.ward_name}
                  </div>
                  <div className="text-[11px] text-slate-500">{w.district}</div>
                </div>
                <div className="col-span-5 md:col-span-7">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-700 rounded-full"
                      style={{ width: `${w.coverage_pct}%` }}
                    />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 font-mono">
                    {w.surveys} surveys · {w.booths} booths
                  </div>
                </div>
                <div className="col-span-3 md:col-span-2 text-right metric-value text-slate-900">
                  {w.coverage_pct}%
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="wi-card p-6">
          <div className="data-label">Action Required</div>
          <div className="mt-3 space-y-3">
            <Link
              to="/admin/users"
              className="block p-3 bg-blue-50 border border-blue-100 rounded-md hover:bg-blue-100 transition-colors"
              data-testid="action-pending-requests"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-900">
                  Pending candidate requests
                </span>
                <Pill tone="blue">{data.pending_requests}</Pill>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Review & create candidate logins
              </div>
            </Link>
            <Link
              to="/admin/analytics"
              className="block p-3 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-900 flex items-center gap-2">
                  <TrendingUp size={14} /> Open analytics
                </span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Ward & booth-level intelligence
              </div>
            </Link>
            <Link
              to="/admin/wards"
              className="block p-3 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-900 flex items-center gap-2">
                  <MapPin size={14} /> Manage wards & booths
                </span>
              </div>
            </Link>
          </div>
        </div>
      </div>

      <div className="wi-card p-6 bg-blue-50/40 border-blue-200 flex items-start gap-3">
        <TrendingUp className="text-blue-700 mt-0.5" size={18} />
        <div className="text-sm text-slate-800">
          <strong>Tip:</strong> Use{" "}
          <Link to="/admin/submissions" className="underline font-medium" data-testid="overview-submissions-link">
            Submissions
          </Link>{" "}
          to inspect individual survey responses,{" "}
          <Link to="/admin/analytics" className="underline font-medium">
            Analytics
          </Link>{" "}
          for ward-level intelligence, and{" "}
          <Link to="/admin/monetization" className="underline font-medium">
            Monetization → Danger Zone
          </Link>{" "}
          to factory-reset data when needed.
        </div>
      </div>
    </div>
  );
}
